/**
 * Migration 004: De-duplicate recurring task reminders.
 *
 * Bug: Both client and server could independently create reminder entries for
 * the same recurring instance (race between generateFixedInstances calls before
 * sync propagated). Each used crypto.randomUUID() → two reminders with different
 * IDs for the same taskId.
 *
 * Fix: For each taskId that has multiple active reminders, keep only the one
 * with the latest fireAt and soft-delete the rest. Also re-key the survivor
 * to the deterministic format `rem_${taskId}` if it isn't already.
 */

import { db } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import type { DataMigration } from "./migrationTypes"

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

async function findDuplicates(): Promise<Map<string, string[]>> {
  const reminders = await db.reminder.where("isDeleted").equals(0).toArray()
  const byTask = new Map<string, string[]>()
  for (const r of reminders) {
    if (!r.taskId) continue
    const list = byTask.get(r.taskId)
    if (list) list.push(r.id!)
    else byTask.set(r.taskId, [r.id!])
  }
  // Only return tasks with >1 reminder
  const duplicates = new Map<string, string[]>()
  byTask.forEach((ids, taskId) => {
    if (ids.length > 1) duplicates.set(taskId, ids)
  })
  return duplicates
}

export const dedupRecurringRemindersMigration: DataMigration = {
  id: "004-dedup-recurring-reminders",
  description: "Remove duplicate reminders for recurring task instances",
  priority: 12, // After other migrations, low urgency

  async needsMigration(): Promise<boolean> {
    const dups = await findDuplicates()
    return dups.size > 0
  },

  async execute(): Promise<number> {
    const dups = await findDuplicates()
    if (dups.size === 0) return 0

    const now = new Date().toISOString()
    let cleaned = 0

    for (const [taskId, ids] of dups) {
      // Fetch all reminder records
      const reminders = await db.reminder.bulkGet(ids)
      const valid = reminders.filter((r): r is NonNullable<typeof r> => !!r)
      if (valid.length <= 1) continue

      // Keep the one with the latest fireAt
      valid.sort((a, b) => (parseDbDate(b.fireAt) ?? 0) - (parseDbDate(a.fireAt) ?? 0))
      const [keeper, ...extras] = valid

      // Soft-delete extras
      await db.reminder.bulkUpdate(
        extras.map((r) => ({
          key: r.id!,
          changes: { isDeleted: 1 as const, deletedAt: now, updatedAt: now },
        })),
      )
      cleaned += extras.length

      // If keeper doesn't have deterministic ID, create a new entry with `rem_${taskId}`
      const deterministicId = `rem_${taskId}`
      if (keeper!.id !== deterministicId) {
        const existing = await db.reminder.get(deterministicId)
        if (!existing) {
          await db.reminder.add({ ...keeper!, id: deterministicId })
          // Soft-delete old keeper
          await db.reminder.update(keeper!.id!, {
            isDeleted: 1 as const,
            deletedAt: now,
            updatedAt: now,
          })
        }
      }
    }

    if (cleaned > 0) {
      console.info(`[migration:004] Cleaned ${cleaned} duplicate reminders across ${dups.size} tasks`)
    }

    return cleaned
  },
}
